import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

function TuitionStudentManager({ tuitionTeacher, allStudents, adminName, onClose }) {
  const [assignedStudentIds, setAssignedStudentIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');

  useEffect(() => {
    fetchCurrentAssignments();
  }, []);

  const fetchCurrentAssignments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/tuition-assignments/${tuitionTeacher._id}`);
      const currentAssignments = response.data.studentIds || [];
      setAssignedStudentIds(currentAssignments.map(student => student._id || student));
    } catch (error) {
      console.error('Error fetching current assignments:', error);
      setAssignedStudentIds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentToggle = (studentId) => {
    setAssignedStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const saveAssignments = async () => {
    try {
      setSaving(true);
      await axios.post(`${API_BASE_URL}/api/tuition-assignments`, {
        tuitionTeacherId: tuitionTeacher._id,
        studentIds: assignedStudentIds,
        assignedBy: adminName
      });
      alert(`Successfully updated student assignments for ${tuitionTeacher.name}!`);
      onClose();
    } catch (error) {
      console.error('Error saving assignments:', error);
      alert('Error saving assignments: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getFilteredStudents = () => {
    let filtered = allStudents;

    if (selectedClass !== 'all') {
      filtered = filtered.filter(student => student.class === selectedClass);
    }

    if (searchTerm) {
      filtered = filtered.filter(student =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.studentId && student.studentId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (student.fathersName && student.fathersName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered;
  };

  const getUniqueClasses = () => {
    const classes = [...new Set(allStudents.map(student => student.class).filter(Boolean))];
    return classes.sort();
  };

  const filteredStudents = getFilteredStudents();
  const uniqueClasses = getUniqueClasses();

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          background: 'white',
          padding: '30px',
          borderRadius: '15px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <h3>Loading current assignments...</h3>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.5)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '15px',
        width: '95%',
        maxWidth: '900px',
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          paddingBottom: '15px',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#333' }}>🎯 Manage Tuition Students</h3>
            <p style={{ margin: '5px 0 0 0', color: '#666' }}>
              Assign students to <strong>{tuitionTeacher.name}</strong>
            </p>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ✕ Close
          </button>
        </div>

        {/* Stats */}
        <div style={{ 
          display: 'flex', 
          gap: '15px', 
          marginBottom: '20px',
          padding: '15px',
          background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
          borderRadius: '10px',
          border: '2px solid #4caf50'
        }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
              {assignedStudentIds.length}
            </div>
            <div style={{ fontSize: '12px', color: '#2e7d32' }}>Currently Assigned</div>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
              {allStudents.length}
            </div>
            <div style={{ fontSize: '12px', color: '#2e7d32' }}>Total Students</div>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
              {uniqueClasses.length}
            </div>
            <div style={{ fontSize: '12px', color: '#2e7d32' }}>Available Classes</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: '15px', 
          marginBottom: '20px',
          padding: '15px',
          background: '#f8f9fa',
          borderRadius: '8px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '200px' }}>
            <label style={{ fontWeight: 'bold', color: '#333' }}>🔍 Search:</label>
            <input 
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, ID..."
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: 'bold', color: '#333' }}>📚 Class:</label>
            <select 
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '120px'
              }}
            >
              <option value="all">All Classes</option>
              {uniqueClasses.map(className => (
                <option key={className} value={className}>{className}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Student List */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          border: '1px solid #e0e0e0', 
          borderRadius: '8px',
          background: '#fafafa'
        }}>
          {filteredStudents.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>📚</div>
              <h4>No students found</h4>
              <p>Try adjusting your search or class filter.</p>
            </div>
          ) : (
            <div style={{ padding: '10px' }}>
              {filteredStudents.map(student => {
                const isAssigned = assignedStudentIds.includes(student._id);
                return (
                  <div 
                    key={student._id} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '12px', 
                      background: isAssigned ? '#e8f5e8' : 'white', 
                      borderRadius: '8px', 
                      marginBottom: '8px',
                      border: isAssigned ? '2px solid #4caf50' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => handleStudentToggle(student._id)}
                  >
                    <div style={{ 
                      width: '24px', 
                      height: '24px', 
                      border: '2px solid #4caf50', 
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      background: isAssigned ? '#4caf50' : 'white',
                      color: isAssigned ? 'white' : 'transparent',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}>
                      ✓
                    </div>
                    
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      border: '2px solid #2196f3', 
                      background: student.profilePicture ? `url(${student.profilePicture})` : '#f0f0f0', 
                      backgroundSize: 'cover', 
                      backgroundPosition: 'center', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '16px', 
                      borderRadius: '50%',
                      marginRight: '12px'
                    }}>
                      {!student.profilePicture && '👤'}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '16px', color: '#333' }}>{student.name}</strong>
                        <span style={{ 
                          background: '#2196f3', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '4px', 
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          {student.class || 'No Class'}
                          {student.section && ` - ${student.section}`}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {student.email}
                        {student.studentId && ` | ID: ${student.studentId}`}
                        {student.fathersName && ` | Father: ${student.fathersName}`}
                      </div>
                    </div>
                    
                    <div style={{ 
                      padding: '4px 8px', 
                      background: isAssigned ? '#4caf50' : '#e0e0e0',
                      color: isAssigned ? 'white' : '#666',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {isAssigned ? 'ASSIGNED' : 'CLICK TO ASSIGN'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          marginTop: '20px', 
          paddingTop: '15px', 
          borderTop: '2px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            <strong>{assignedStudentIds.length}</strong> students selected for assignment
            {filteredStudents.length !== allStudents.length && (
              <span> (showing {filteredStudents.length} of {allStudents.length})</span>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => {
                setAssignedStudentIds([]);
              }}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🗑️ Clear All
            </button>
            
            <button 
              onClick={() => {
                const allStudentIds = filteredStudents.map(s => s._id);
                setAssignedStudentIds(allStudentIds);
              }}
              style={{
                background: '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              ✅ Select All Visible
            </button>
            
            <button 
              onClick={saveAssignments}
              disabled={saving}
              style={{
                background: saving ? '#6c757d' : 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              {saving ? '⏳ Saving...' : '💾 Save Assignments'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TuitionStudentManager;